import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import type { PaletteGroup, PaletteItem } from '@/lib/protocol-step-draft';
import type { ProtocolVariable } from '@matter_extractor/shared';

/**
 * The list the `@` and the `{ }` button open (refonte modulaire §7.2, §8.6;
 * maquette `paletteHTML`).
 *
 * « La palette n'offre que ce qu'un protocole connaît » - its own objectives,
 * those of the steps before it, and the criteria. Three groups, categories
 * aligned left and the variables indented under them, so the eye finds the
 * category first and the name second; a homonym carries its step so the two
 * lines are told apart at all.
 *
 * The groups themselves are computed by `paletteGroups`, which is pure and
 * tested: which objectives are reachable and how a homonym is worded is a rule,
 * and a rule assembled in a render is a rule nothing asserts. What is here is
 * only the drawing.
 *
 * An EMPTY group still shows its title and says « aucun » under it. The three
 * groups are the shape of what a protocol can cite, and hiding one would make
 * a palette with two headings read as a palette that forgot something.
 *
 * ## The palette of the reading draws here too (#158, W1)
 *
 * « La palette est celle des directions et s'ouvre où ce composant l'ouvre »
 * (noyau.md §4.9): the reading for the analyst hands its own groups - one per
 * step with a named objective, `readingPaletteGroups` - and no group at all
 * when there is nothing to offer, so it says one sentence instead (`empty`),
 * under its own mark (`testId`: the two views of the editor stay mounted).
 *
 * A palette is as long as the protocol is wide - every named objective of it,
 * for the reading - so its height is bounded and it scrolls inside, for both
 * palettes. A scroller clips what its children paint outside their box, so the
 * focus ring of a full-width line is drawn INSIDE it (`ring-inset`): outside,
 * its two sides were cut away even with nothing to scroll. The reading's field
 * is bounded too (4000 characters, tokens included), and a line whose token
 * would pass that bound is GREYED, never inserted and cut (`fits`), under one
 * sentence that says why (`full`).
 */

export interface VariablePaletteProps {
  groups: readonly PaletteGroup[];
  /**
   * The letters typed after the `@`, or null when the palette was opened by the
   * `{ }` button - which is what decides whether the query line is drawn.
   */
  query: string | null;
  onPick: (variable: ProtocolVariable) => void;
  /** What a palette with NO group says - the reading's (#158); a direction always has three. */
  empty?: string;
  /** Its mark: the directions' by default, the reading's own beside it (#158). */
  testId?: string;
  /**
   * Whether a variable's token still holds under the field's bound - absent
   * where the field has none, which is every field but the reading (#158).
   */
  fits?: (variable: ProtocolVariable) => boolean;
  /** What the palette says above its lines when `fits` greys one of them. */
  full?: string;
}

/** An objective's chip and a criterion's are the two inks of §8.6. */
const CHIP_CLASS: Readonly<Record<ProtocolVariable['kind'], string>> = {
  objective: 'bg-bleu-nuit-tint text-bleu-nuit',
  criterion: 'bg-vert-tint text-vert-deep',
};

export function VariablePalette({
  groups,
  query,
  onPick,
  empty,
  testId = PROTOCOL_TEST_IDS.palette,
  fits,
  full,
}: VariablePaletteProps) {
  const refused = (item: PaletteItem): boolean => fits !== undefined && !fits(item.variable);
  const anyRefused = groups.some((group) => group.items.some(refused));
  return (
    <div
      data-testid={testId}
      className="absolute right-2 bottom-12 z-30 max-h-[min(24rem,60vh)] w-80 overflow-y-auto rounded-md border-2 border-gray-200 bg-white py-2 shadow-lg"
    >
      {query === null ? null : (
        <p className="flex items-center gap-2 px-3 pb-2 text-caption text-gray-500">
          <span>
            @<span className="font-semibold text-gray-900">{query}</span>
          </span>
          <span className="ml-auto">Entrée insère · Échap ferme</span>
        </p>
      )}
      {groups.length === 0 && empty !== undefined ? (
        <p className="px-3 py-1 text-caption text-gray-700 leading-4">{empty}</p>
      ) : null}
      {anyRefused && full !== undefined ? (
        <p className="px-3 py-1 text-caption text-gray-700 leading-4">{full}</p>
      ) : null}
      {groups.map((group) => (
        <div key={group.id ?? group.title}>
          <p className="px-2 pt-2 pb-0.5 font-semibold text-gray-500 text-micro uppercase tracking-wide">
            {group.title}
          </p>
          {group.items.length === 0 ? (
            <p className="px-5 pb-1 text-caption text-gray-500">aucun</p>
          ) : (
            group.items.map((item) => (
              <PaletteLine
                key={`${item.variable.kind}:${item.variable.id}`}
                item={item}
                refused={refused(item)}
                onPick={onPick}
              />
            ))
          )}
        </div>
      ))}
    </div>
  );
}

function PaletteLine({
  item,
  refused,
  onPick,
}: { item: PaletteItem; refused: boolean; onPick: VariablePaletteProps['onPick'] }) {
  return (
    <button
      type="button"
      data-testid={PROTOCOL_TEST_IDS.paletteItem}
      data-variable-id={item.variable.id}
      // `aria-disabled` and not `disabled`: a disabled button swallows the
      // mousedown below, and the field would lose its caret to a line that
      // inserts nothing anyway.
      aria-disabled={refused ? true : undefined}
      // The field must not blur before the click lands: a blur would close the
      // palette and take the `@query` spot with it.
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => {
        if (!refused) onPick(item.variable);
      }}
      className="flex w-full cursor-pointer items-center gap-2 rounded-sm py-1.5 pr-2 pl-5 text-left text-small hover:bg-bleu-nuit-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit focus-visible:ring-inset aria-disabled:cursor-not-allowed aria-disabled:text-gray-600 aria-disabled:hover:bg-transparent"
    >
      <span
        className={`inline-block rounded-full px-2 font-semibold text-caption ${refused ? 'bg-gray-100 text-gray-600' : CHIP_CLASS[item.variable.kind]}`}
      >
        @{item.label}
      </span>
      {/* A homonym's meta is the one thing that tells two identical chips apart,
          so it is inked rather than merely written - the mockup's `.meta.dup`. */}
      <span
        className={`ml-auto whitespace-nowrap text-caption ${item.duplicate ? 'text-jaune-deep' : 'text-gray-500'}`}
      >
        {item.meta}
      </span>
    </button>
  );
}
